import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Plus, Minus, Camera, Image, Calculator, Shield, CreditCard, Lock, Gift, FlaskConical, BrainCircuit } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TRIAL_CREDITS = 600;
const TRIAL_CAMERA_PRICE = 495;

interface CameraVariant {
  id: string;
  name: string;
  price: number;
  description: string;
  hidden?: boolean;
}

const cameraVariants: CameraVariant[] = [
  {
    id: "ssc100",
    name: "Day Vision SSC100",
    price: 195,
    description: "Standard day vision camera",
    hidden: true
  },
  {
    id: "ssc200",
    name: "Day & Night Vision Camera SSC200",
    price: 295,
    description: "Enhanced day & night vision camera"
  }
];

const visibleCameraVariants = cameraVariants.filter(c => !c.hidden);

interface PlanTier {
  name: string;
  imagesPerDay: number;
  imagesPerYear: number;
  pricePerImage: number;
  description: string;
}

const planTiers: PlanTier[] = [
  {
    name: "Trial",
    imagesPerDay: 0,
    imagesPerYear: 600,
    pricePerImage: 0,
    description: "Camera + 600 credits, continue or return"
  },
  {
    name: "Core", 
    imagesPerDay: 3,
    imagesPerYear: 1095,
    pricePerImage: 0.39,
    description: "3 images per day – ideal for standard monitoring"
  },
  {
    name: "Pro",
    imagesPerDay: 6, 
    imagesPerYear: 2190,
    pricePerImage: 0.35,
    description: "6 images per day – enhanced visibility"
  },
  {
    name: "Plus",
    imagesPerDay: 8,
    imagesPerYear: 2920,
    pricePerImage: 0.29,
    description: "8 images per day – maximum coverage"
  }
];

interface CustomerDealCalculatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSubmit?: (data: any) => void;
  onSaveAndClose?: (data: any) => void;
  isEditing?: boolean;
}

export const CustomerDealCalculator = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  onSaveAndClose,
  isEditing,
}: CustomerDealCalculatorProps) => {
  const { toast } = useToast();
  const fmtGBP = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  const [selectedCamera, setSelectedCamera] = useState<CameraVariant>(visibleCameraVariants[0]);
  const [cameraQuantity, setCameraQuantity] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>(planTiers[0]);
  const [credits, setCredits] = useState(1095);
  const [commitmentYears, setCommitmentYears] = useState<3 | 5>(3);
  const [paymentType, setPaymentType] = useState<'annual' | 'upfront'>('annual');
  const [customAiModel, setCustomAiModel] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountTypes, setDiscountTypes] = useState<string[]>([]);
  const [accessories, setAccessories] = useState<Array<{ id: string; name: string; quantity: number; unit_price: number }>>([]);

  // Hydrate state from initialData each time the dialog opens
  useEffect(() => {
    if (!open || !initialData) return;

    const cam = visibleCameraVariants.find(c => c.id === initialData.camera_model_id);
    if (cam) setSelectedCamera(cam);
    else setSelectedCamera(visibleCameraVariants[0]);

    setCameraQuantity(Math.max(1, Number(initialData.cameras) || 1));

    const planName = initialData.plan;
    const plan = planTiers.find(p => p.name === planName);
    const resolvedPlan = plan || planTiers.find(p => p.name === 'Core') || planTiers[0];
    setSelectedPlan(resolvedPlan);

    if (resolvedPlan.name === 'Trial') {
      setCredits(TRIAL_CREDITS);
    } else {
      const c = Number(initialData.credits);
      setCredits(Number.isFinite(c) && c > 0 ? c : resolvedPlan.imagesPerYear);
    }

    setCommitmentYears(initialData.commitment_years === 5 ? 5 : 3);
    setPaymentType(initialData.payment_structure === 'upfront' ? 'upfront' : 'annual');
    setCustomAiModel(Boolean(initialData.custom_ai_model));

    setDiscountAmount(Number(initialData.discount_amount) || 0);
    setDiscountTypes(Array.isArray(initialData.discount_types) ? initialData.discount_types : []);
    setAccessories(Array.isArray(initialData.accessories) ? initialData.accessories : []);
  }, [open, initialData]);

  const isTrial = selectedPlan.name === 'Trial';
  const cameraPrice = isTrial ? TRIAL_CAMERA_PRICE : (commitmentYears === 5 ? 0 : selectedCamera.price);
  const cameraCost = cameraQuantity * cameraPrice;
  const originalCameraUnitPrice = isTrial ? TRIAL_CAMERA_PRICE : selectedCamera.price;
  const originalCameraCost = cameraQuantity * originalCameraUnitPrice;
  const cameraIsFree = !isTrial && cameraPrice === 0 && originalCameraUnitPrice > 0;
  const cameraSavings = Math.max(0, originalCameraCost - cameraCost);
  const pricePerImage = isTrial ? 0 : selectedPlan.pricePerImage + (customAiModel ? 0.10 : 0);
  const totalCreditsPerYear = isTrial ? TRIAL_CREDITS : credits * cameraQuantity;
  const coverageYears = isTrial ? 1 : commitmentYears;
  
  const annualCreditsCost = isTrial ? 0 : totalCreditsPerYear * pricePerImage;
  const year1Subtotal = cameraCost + annualCreditsCost;

  const accessoriesCost = accessories.reduce((sum, a) => sum + (Number(a.unit_price) || 0) * (Number(a.quantity) || 0), 0);

  const subtotal = isTrial
    ? cameraCost
    : (paymentType === 'annual' ? year1Subtotal : (annualCreditsCost * coverageYears + cameraCost));
  const total = Math.max(0, subtotal + accessoriesCost - discountAmount);

  const totalDays = 365 * coverageYears;
  const shotsPerDay = isTrial ? Math.floor(TRIAL_CREDITS / 365) : Math.round(credits / 365);
  const totalContractCost = isTrial ? cameraCost : (cameraCost + (annualCreditsCost * coverageYears));
  const perCameraCount = Math.max(1, cameraQuantity);
  const averageCostPerDay = totalContractCost / totalDays / perCameraCount;
  const averageCostPerWeek = averageCostPerDay * 7;
  const averageCostPerMonth = averageCostPerDay * 30;

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, cameraQuantity + delta);
    setCameraQuantity(newQuantity);
  };

  const handlePlanSelect = (plan: PlanTier) => {
    setSelectedPlan(plan);
    if (plan.name !== 'Trial') {
      setCredits(plan.imagesPerYear);
    }
    toast({
      title: `${plan.name} Plan Selected!`,
      description: plan.description,
    });
  };

  const handleCreditsChange = (value: number[]) => {
    setCredits(value[0]);
  };

  const buildPayload = () => {
    const effectiveCameraQuantity = cameraQuantity;
    const totalCreditsNeeded = isTrial
      ? TRIAL_CREDITS
      : (paymentType === 'upfront' ? totalCreditsPerYear * coverageYears : totalCreditsPerYear);
    const title = isTrial
      ? `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${TRIAL_CREDITS.toLocaleString()} Credits (Trial)`
      : `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;

    return {
      title,
      value: total,
      total_cameras: effectiveCameraQuantity,
      total_images: totalCreditsNeeded,
      total_revenue: total,
      image_revenue: isTrial ? 0 : annualCreditsCost,
      discount_amount: discountAmount,
      deal_data: {
        order_type: 'cameras_and_credits',
        payment_structure: isTrial ? 'upfront' : paymentType,
        camera_model: selectedCamera.name,
        camera_model_id: selectedCamera.id,
        camera_unit_price: cameraPrice,
        plan: selectedPlan.name,
        credits_per_camera: isTrial ? TRIAL_CREDITS : credits,
        price_per_image: pricePerImage,
        camera_cost: cameraCost,
        credits_cost: isTrial ? 0 : annualCreditsCost,
        coverage_years: coverageYears,
        commitment_years: isTrial ? 1 : commitmentYears,
        shots_per_day: shotsPerDay,
        images_per_day: isTrial ? 0 : selectedPlan.imagesPerDay,
        annual_payment_breakdown: !isTrial && paymentType === 'annual' ? {
          year1_payment: total,
          subsequent_annual_payment: annualCreditsCost,
          contract_years: coverageYears,
          annual_credits: totalCreditsPerYear
        } : null,
        customerGenerated: true,
        custom_ai_model: customAiModel,
        discount_amount: discountAmount,
        discount_types: discountTypes,
        accessories,
        accessories_cost: accessoriesCost,
      }
    };
  };

  const handleAcceptClick = () => {
    onSubmit?.(buildPayload());
  };

  const handleSaveClick = () => {
    onSaveAndClose?.(buildPayload());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Create Your Order
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Configure your cameras and credits package
          </p>
        </DialogHeader>

        {(cameraIsFree || discountAmount > 0) && (
          <div className="mt-4 rounded-lg border-2 border-green-400 bg-green-50 p-4">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-green-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-green-900">
                  Discount applied — you save £{fmtGBP(cameraSavings + discountAmount)}
                </div>
                <div className="text-xs text-green-800 mt-1 space-y-0.5">
                  {cameraIsFree && (
                    <div>
                      • {cameraQuantity}x {selectedCamera.name} camera{cameraQuantity > 1 ? 's' : ''} included free
                      {commitmentYears === 5 ? ' (5-year commitment)' : ''} — saving £{fmtGBP(cameraSavings)}
                    </div>
                  )}
                  {discountAmount > 0 && (
                    <div>
                      • Additional discount{discountTypes.length > 0 ? ` (${discountTypes.join(', ')})` : ''}: −£{fmtGBP(discountAmount)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Left Column - Configuration */}
          <div className="space-y-6">
            {/* 1. Camera Model Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  Camera Model
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleCameraVariants.map((camera) => (
                    <div
                      key={camera.id}
                      onClick={() => {
                        setSelectedCamera(camera);
                        toast({ title: `${camera.name} Selected!`, description: camera.description });
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedCamera.id === camera.id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-border hover:border-teal-300 hover:bg-accent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-foreground">{camera.name}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{camera.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-teal-600">£{camera.price}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 2. Camera Quantity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5" />
                  Camera Quantity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={cameraQuantity <= 1}
                    className="h-12 w-12 rounded-full"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  
                  <div className="text-center">
                    <Input
                      type="number"
                      min={1}
                      value={cameraQuantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        setCameraQuantity(Math.max(1, value));
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value);
                        if (isNaN(value) || value < 1) setCameraQuantity(1);
                      }}
                      className="text-3xl font-bold w-24 text-center mb-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="text-sm text-muted-foreground">
                      Camera{cameraQuantity > 1 ? 's' : ''} @ £{cameraPrice} each
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(1)}
                    className="h-12 w-12 rounded-full"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* 3. Plan Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Choose Your Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {planTiers.map((plan) => (
                  <div
                    key={plan.name}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedPlan.name === plan.name
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          {plan.name}
                          {selectedPlan.name === plan.name && (
                            <Badge variant="default">Selected</Badge>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{plan.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          {plan.name === 'Trial' ? '£495 camera' : `£${plan.pricePerImage}/image`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {plan.name === 'Trial' ? '600 credits included' : `${plan.imagesPerYear.toLocaleString()}/year`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Custom AI Model - Hidden for Trial */}
            {!isTrial && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BrainCircuit className="h-5 w-5" />
                  Custom AI Model
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="font-medium">Enable Custom AI Model</Label>
                    <p className="text-sm text-muted-foreground">
                      Adds £0.10/image for enhanced AI image analysis
                    </p>
                    {customAiModel && (
                      <p className="text-sm font-semibold text-teal-700">
                        Effective per-image price: £{(selectedPlan.pricePerImage + 0.10).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={customAiModel}
                    onCheckedChange={setCustomAiModel}
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* 4. Image Slider - Hidden for Trial */}
            {!isTrial && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Images per Camera (per year)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="font-medium">Images: {credits.toLocaleString()}</Label>
                    <div className="text-sm text-muted-foreground">
                      ~{shotsPerDay} images/day
                    </div>
                  </div>
                  <Slider
                    value={[credits]}
                    onValueChange={handleCreditsChange}
                    min={selectedPlan.imagesPerYear}
                    max={20000}
                    step={20}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{selectedPlan.imagesPerYear.toLocaleString()} (included)</span>
                    <span>20,000</span>
                  </div>
                  <div className="bg-accent rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Included with {selectedPlan.name}:</span>
                      <span className="font-semibold">{selectedPlan.imagesPerYear.toLocaleString()} images/year</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* 5. Commitment Period - Hidden for Trial */}
            {!isTrial && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Commitment Period
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setCommitmentYears(3)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      commitmentYears === 3
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-border hover:border-teal-300'
                    }`}
                  >
                    <div className="font-semibold text-lg">3 Years</div>
                    <p className="text-sm text-muted-foreground mt-1">Standard commitment</p>
                    {commitmentYears === 3 && (
                      <Badge className="bg-teal-primary text-black text-xs mt-2">Selected</Badge>
                    )}
                  </button>
                  <button
                    onClick={() => setCommitmentYears(5)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      commitmentYears === 5
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-border hover:border-teal-300'
                    }`}
                  >
                    <div className="font-semibold text-lg">5 Years</div>
                    <p className="text-sm text-muted-foreground mt-1">Best value</p>
                    <div className="flex items-center gap-1 mt-2">
                      <Gift className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-600">Camera included FREE!</span>
                    </div>
                    {commitmentYears === 5 && (
                      <Badge className="bg-teal-primary text-black text-xs mt-2">Selected</Badge>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* 6. Payment Structure Selection - hidden for Trial */}
            {!isTrial && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Structure
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={paymentType} onValueChange={(value) => setPaymentType(value as 'annual' | 'upfront')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="annual" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Annual Contract
                    </TabsTrigger>
                    <TabsTrigger value="upfront" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Upfront Payment
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="annual" className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Spread payments over {coverageYears} years with annual commitments. Lower Year 1 payment includes cameras + first year credits.
                    </p>
                  </TabsContent>
                  <TabsContent value="upfront" className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Single payment for full {coverageYears}-year coverage. All credits allocated immediately.
                    </p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            )}

          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(cameraIsFree || discountAmount > 0) && (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                    <div className="text-sm font-semibold text-green-800 mb-1">
                      Discount applied to your order
                    </div>
                    <div className="text-xs text-green-700 space-y-0.5">
                      {cameraIsFree && (
                        <div className="flex justify-between">
                          <span>
                            {cameraQuantity}x {selectedCamera.name} camera{cameraQuantity > 1 ? 's' : ''} included free
                            {commitmentYears === 5 ? ' (5-year commitment)' : ''}
                          </span>
                          <span className="font-semibold">−£{fmtGBP(cameraSavings)}</span>
                        </div>
                      )}
                      {discountAmount > 0 && (
                        <div className="flex justify-between">
                          <span>
                            Additional discount{discountTypes.length > 0 ? ` (${discountTypes.join(', ')})` : ''}
                          </span>
                          <span className="font-semibold">−£{fmtGBP(discountAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-green-200 pt-1 mt-1">
                        <span className="font-semibold">Total saved</span>
                        <span className="font-bold">−£{fmtGBP(cameraSavings + discountAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}
                {isTrial ? (
                  <>
                    {/* Trial Summary */}
                    <div className="bg-accent border border-border rounded-lg p-3 mb-4">
                      <div className="text-sm font-semibold mb-2">Trial Plan</div>
                      <div className="text-xs text-muted-foreground">£495 camera (continue or return) + 600 credits included</div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>{cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}</span>
                        <span>£{fmtGBP(cameraCost)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>{TRIAL_CREDITS} Credits included</span>
                        <span>Included</span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total</span>
                          <span>£{fmtGBP(total)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-center mt-2">
                          All amounts are ex VAT
                        </div>
                      </div>
                    </div>
                  </>
                ) : paymentType === 'annual' ? (
                  <>
                    {/* Annual Contract Breakdown */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="text-sm font-semibold text-blue-800 mb-2">Annual Contract - {coverageYears} Year Commitment</div>
                      <div className="text-xs text-blue-600">
                        {selectedPlan.name} Plan • {selectedPlan.imagesPerDay} images/day
                        {commitmentYears === 5 && ' • Camera FREE'}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="font-semibold border-b border-slate-200 pb-2">Year 1 Payment</div>
                      
                      <div className="flex justify-between text-sm">
                        <span>
                          {cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}
                          {cameraIsFree && <span className="ml-1 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800 uppercase tracking-wide">Free</span>}
                        </span>
                        <span>
                          {cameraIsFree && (
                            <span className="text-muted-foreground line-through mr-2">£{fmtGBP(originalCameraCost)}</span>
                          )}
                          £{fmtGBP(cameraCost)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span>Year 1 Credits ({totalCreditsPerYear.toLocaleString()})</span>
                        <span>£{fmtGBP(annualCreditsCost)}</span>
                      </div>

                      <div className="border-t pt-2">
                        <div className="flex justify-between text-sm">
                          <span>Year 1 Subtotal</span>
                          <span>£{fmtGBP(year1Subtotal)}</span>
                        </div>
                        <div className="flex justify-between font-bold">
                          <span>Year 1 Total</span>
                          <span>£{fmtGBP(total)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-center mt-2">
                          All amounts are ex VAT
                        </div>
                      </div>
                    </div>

                    {coverageYears > 1 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <div className="text-sm font-semibold mb-2">Subsequent Years ({coverageYears - 1} years)</div>
                        <div className="flex justify-between text-sm">
                          <span>Annual Credits ({totalCreditsPerYear.toLocaleString()})</span>
                          <span>£{fmtGBP(annualCreditsCost)}</span>
                        </div>
                        <div className="flex justify-between font-bold border-t pt-2">
                          <span>Annual Payment</span>
                          <span>£{fmtGBP(annualCreditsCost)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-center mt-2">
                          All amounts are ex VAT
                        </div>
                      </div>
                    )}

                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                      <div className="text-sm font-semibold text-teal-800">Total Contract Value</div>
                      <div className="text-lg font-bold text-teal-800">
                        £{fmtGBP(total + (coverageYears - 1) * annualCreditsCost)}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Upfront Payment Breakdown */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="text-sm font-semibold text-green-800 mb-2">Upfront Payment - {coverageYears} Year Coverage</div>
                      <div className="text-xs text-green-600">
                        All credits allocated immediately
                        {commitmentYears === 5 && ' • Camera FREE'}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>
                          {cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}
                          {cameraIsFree && <span className="ml-1 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800 uppercase tracking-wide">Free</span>}
                        </span>
                        <span>
                          {cameraIsFree && (
                            <span className="text-muted-foreground line-through mr-2">£{fmtGBP(originalCameraCost)}</span>
                          )}
                          £{fmtGBP(cameraCost)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{(totalCreditsPerYear * coverageYears).toLocaleString()} Credits ({coverageYears} year{coverageYears > 1 ? 's' : ''})</span>
                        <span>£{fmtGBP(annualCreditsCost * coverageYears)}</span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between">
                          <span>Subtotal</span>
                          <span>£{fmtGBP(annualCreditsCost * coverageYears + cameraCost)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground text-center mt-2">
                          All amounts are ex VAT
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Total Upfront Payment</span>
                          <span>£{fmtGBP(total)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Cost Breakdown */}
                <div className="bg-accent border border-border rounded-lg p-3">
                  <div className="text-sm space-y-1">
                    {accessories.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Accessories ({accessories.reduce((s, a) => s + (Number(a.quantity) || 0), 0)} item{accessories.length > 1 ? 's' : ''}):</span>
                        <span className="font-semibold">£{fmtGBP(accessoriesCost)}</span>
                      </div>
                    )}
                    {discountAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount applied{discountTypes.length > 0 ? ` (${discountTypes.join(', ')})` : ''}:</span>
                        <span className="font-semibold text-red-600">-£{fmtGBP(discountAmount)}</span>
                      </div>
                    )}
                    {customAiModel && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Custom AI Model:</span>
                        <span className="font-semibold text-teal-700">+£0.10/image</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Per-image cost:</span>
                      <span className="font-semibold">£{pricePerImage.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg. cost/day/camera:</span>
                      <span className="font-semibold">£{fmtGBP(averageCostPerDay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg. cost/week/camera:</span>
                      <span className="font-semibold">£{fmtGBP(averageCostPerWeek)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Avg. cost/month/camera:</span>
                      <span className="font-semibold">£{fmtGBP(averageCostPerMonth)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Plan:</span>
                      <span className="font-semibold text-teal-700">{selectedPlan.name}</span>
                    </div>
                    {!isTrial && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Commitment:</span>
                        <span className="font-semibold text-teal-700">{coverageYears} years</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 space-y-2">
                  <Button className="w-full" size="lg" onClick={handleAcceptClick}>
                    {isEditing ? 'Save & Accept Proposal' : 'Create Order'}
                  </Button>
                  {onSaveAndClose && (
                    <Button
                      className="w-full"
                      size="lg"
                      variant="outline"
                      onClick={handleSaveClick}
                    >
                      Save Changes & Close
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
